/** Packages **/
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Dimensions from 'react-dimensions';
import CalendarActivities from '../../components/CalendarActivities';
import {
  STAFFID,
  common,
  calendar,
  BREAK_POINT_LARGE
} from '../../data/constants';
import * as actions from './../../data/linearCalendar/actions';
import * as utilities from './../../utilities/calendarUtilities';
import LinearCalendarDay from './LinearCalendarDay';
import { openBidsBuffer } from './../../settings/settings';
import { isCarryInOutDay } from './helpers';
import Loader from './../../components/Loader';
import { useLanguage } from '../../context/LanguageContext';

const LinearCalendarTable = ({
  activities,
  calendarStartDateStr,
  calendarEndDateStr,
  totalDaysInCalendar,
  fetchActivities,
  startDate,
  endDate,
  bidSessionId,
  categoryId,
  containerWidth,
  updateDimensions,
  showNotification,
  getStatistics,
  removeActivity,
  updateActivity,
  handleActionPreference,
  bidPreferencesTotalEntries,
  bidSession,
  totalEntries,
  requestAll,
  reportMode,
  setFromAddButton
}) => {
  const { labels } = useLanguage();
  const commonLabels = labels.commonLabels || {};
  const [state, setState] = useState({
    startDate: [],
    endDate: [],
    fetched: false,
    preassignments: [],
    statistics: {}
  });

  useEffect(() => {
    const staffId = sessionStorage.getItem(STAFFID);
    getStatistics(bidSessionId, categoryId, staffId)
      .then((response) => {
        setState(prevState => ({ ...prevState, statistics: response.data }));
      })
      .catch(error => console.log(error));
    updateDimensions();
  }, [getStatistics, bidSessionId, categoryId, updateDimensions]);

  useEffect(() => {
    if (!state.fetched) {
      setState(prevState => ({
        ...prevState,
        startDate,
        endDate,
        fetched: true
      }));
    }
  }, [startDate, endDate, state.fetched]);

  useEffect(() => {
    if (!state.fetched) {
      const staffId = sessionStorage.getItem(STAFFID);
      if (startDate.length > common.ZERO && !reportMode) {
        fetchActivities(
          staffId,
          calendarStartDateStr,
          calendarEndDateStr,
          bidSessionId,
          categoryId
        );
      }
    }
  }, [state.fetched, startDate, fetchActivities, calendarStartDateStr, calendarEndDateStr, bidSessionId, categoryId]);

  const handleScroll = e => {
    document.getElementById('Open-Time-Helper').scrollLeft =
      e.nativeEvent.target.scrollLeft;
  };

  let totalDays = totalDaysInCalendar;
  const calendarStartDateTs = new Date(calendarStartDateStr).getTime();
  const minContainerWidth = calendar.DAY_MIN_LENGTH * totalDays;
  const container =
    containerWidth < minContainerWidth ? minContainerWidth : containerWidth;
  const staffId = sessionStorage.getItem(STAFFID);
  const preassignments = activities.activities
    ? activities.activities.entries
    : [];
  const types = activities.types ? activities.types : new Map();
  const days = [];

  preassignments.map((preassignment, i) => preassignment.pairings = bidSession?.pairings?.[i])

  if (totalDays < calendar.CALENDAR_MIN_DAYS) {
    totalDays = calendar.CALENDAR_MIN_DAYS;
  } else if (totalDays > calendar.CALENDAR_MAX_DAYS) {
    totalDays = calendar.CALENDAR_MAX_DAYS;
  }

  const calendarDayWidth = !reportMode ? container / totalDaysInCalendar : 27;
  const calendarHourWidth = calendarDayWidth / 24;
  let calendarDay = utilities.getUTC(calendarStartDateTs);
  const statistics = state.statistics.response || {};
  const statisticsEntries = statistics.entries ? statistics.entries : [];

  for (let i = 0; i < totalDays; i += 1) {
    let currentClassName = 'CalendarDay';
    if (
      Number(calendarDay?.getDay()) ===
        common.dates.jsweekdaysnumeric.SATURDAY ||
      Number(calendarDay?.getDay()) === common.dates.jsweekdaysnumeric.SUNDAY
    ) {
      currentClassName += ' WE';
    }
    if (isCarryInOutDay(i, bidSession.bidSessionDays, openBidsBuffer.prevBuffer)) {
      currentClassName += ' CarryInOutDay';
    }

    days.push(
      <LinearCalendarDay
        bidSession={bidSession}
        totalEntries={totalEntries}
        showNotification={showNotification}
        handleActionPreference={handleActionPreference}
        width={calendarDayWidth}
        className={currentClassName}
        id={i}
        key={i}
        dayNumber={calendarDay?.getDate()}
        dayString={commonLabels?.dates?.jsweekdays[calendarDay?.getDay()]}
        calendarDay={calendarDay}
        statistics={statisticsEntries}
        reportMode={reportMode}
      />
    );
    calendarDay = utilities.addDays(calendarDay, 1);
  }

  return (
    <div id='fixed-wrapper-js'>
      <div
        onScroll={handleScroll}
        id='Calendar-Wrapper'
        className={!reportMode ? 'Calendar-Wrapper' : 'Calendar-Wrapper-Report'}
      >
        <div
          id='Calendar-Table'
          className='Calendar-Table'
          style={{ minWidth: minContainerWidth }}
        >
          {days}
        </div>

        {requestAll ? (
          <Loader className={'requestAll-loader'} />
        ) : (
          <CalendarActivities
            categoryId={categoryId}
            bidSessionId={bidSessionId}
            bidSession={bidSession}
            bidSessionStartDate={startDate}
            staffId={staffId}
            activities={preassignments}
            types={types}
            containerWidth={container}
            calendarHourWidth={calendarHourWidth}
            calendarStartDateStr={calendarStartDateStr}
            calendarEndDateStr={calendarEndDateStr}
            pivotStartDate={new Date()}
            removeActivity={removeActivity}
            updateActivity={updateActivity}
            handleActionPreference={handleActionPreference}
            showNotification={showNotification}
            bidPreferencesTotalEntries={bidPreferencesTotalEntries}
            reportMode={reportMode}
            setFromAddButton={setFromAddButton}
          />
        )}
      </div>
    </div>
  );
};

LinearCalendarTable.propTypes = {
  activities: PropTypes.any.isRequired,
  calendarStartDateStr: PropTypes.any,
  calendarEndDateStr: PropTypes.any,
  totalDaysInCalendar: PropTypes.number.isRequired,
  fetchActivities: PropTypes.func.isRequired,
  startDate: PropTypes.array.isRequired,
  endDate: PropTypes.array.isRequired,
  bidSessionId: PropTypes.number.isRequired,
  categoryId: PropTypes.number.isRequired,
  containerWidth: PropTypes.number.isRequired,
  updateDimensions: PropTypes.func,
  showNotification: PropTypes.func.isRequired,
  getStatistics: PropTypes.func.isRequired,
  removeActivity: PropTypes.func.isRequired,
  updateActivity: PropTypes.func.isRequired,
  handleActionPreference: PropTypes.func.isRequired,
  bidPreferencesTotalEntries: PropTypes.func.isRequired,
  bidSession: PropTypes.shape({
    currentBid: PropTypes.object,
    bidPreferences: PropTypes.object
  }),
  totalEntries: PropTypes.number.isRequired,
  requestAll: PropTypes.bool,
  reportMode: PropTypes.bool.isRequired
};

LinearCalendarTable.defaultProps = {
  calendarStartDateStr: null,
  calendarEndDateStr: null,
  linearCalendar: {},
  startDate: [],
  endDate: [],
  bidSessionId: null,
  categoryId: null,
  updateDimensions: function updateDimensions() {},
  requestAll: false,
  reportMode: false
};

const mapStateToProps = state => {
  const currentBid = state.bidSession.bidSession.currentBid || {};
  const bidSessionId = currentBid.bidSessionId;
  const currentCategory =
    state.bidSession.bidSession.user.currentCategory || {};
  const categoryId = currentCategory.categoryId;

  return {
    ...state.linearCalendar,
    bidSessionId,
    categoryId
  };
};

const mapDispatchToProps = dispatch => ({
  fetchActivities: (staffId, startDate, endDate, bidSessionId, categoryId) =>
    dispatch(
      actions.default.fetchActivities(
        staffId,
        startDate,
        endDate,
        bidSessionId,
        categoryId
      )
    ),
  getStatistics: (bidSessionId, categoryId, staffId) =>
    dispatch(actions.default.getStatistics(bidSessionId, categoryId, staffId)),
  removeActivity: activityId =>
    dispatch(actions.default.removeActivity(activityId)),
  updateActivity: activity => dispatch(actions.default.updateActivity(activity))
});

export default Dimensions()(
  connect(mapStateToProps, mapDispatchToProps)(LinearCalendarTable)
);